import httpClient from "@/config/axios";
import type * as type from "@vTypes";
import type {
  SaveWeeklyOffNursePayload,
  WeeklyOffNurseList,
  WeeklyOffSettingConfig,
} from "@vTypes/weeklyOff";

export interface NurseMonthlyLimitItem {
  nurse_id: string;
  group_id: string;
  year: number;
  month: number;

  d_min?: number | null;
  d_max?: number | null;
  d_exact?: number | null;

  e_min?: number | null;
  e_max?: number | null;
  e_exact?: number | null;

  n_min?: number | null;
  n_max?: number | null;
  n_exact?: number | null;

  o_min?: number | null;
  o_max?: number | null;
  o_exact?: number | null;
}

export interface NurseMonthlyLimitWarning {
  code: string;
  message: string;
  severity: "warning";
}

export interface NurseMonthlyLimitMeta {
  target_nurse_count: number;
  active_nurse_count: number;
  override_ratio: number;
  recommended_ratio: number;
}

export interface NurseMonthlyLimitListResponse {
  items: NurseMonthlyLimitItem[];
  meta?: NurseMonthlyLimitMeta | null;
  warnings?: NurseMonthlyLimitWarning[] | null;
}

export interface NurseMonthlyLimitBulkUpsertRequest {
  year: number;
  month: number;
  limits: NurseMonthlyLimitItem[];
}

// 모든 간호사 목록 조회 API
export const fetchFullNurse = async (
  groupId?: string
): Promise<type.FullNurseSchema[]> => {
  return httpClient.get<type.FullNurseSchema[]>("/nurses", {
    params: groupId ? { group_id: groupId } : undefined,
  });
};

// 주휴 설정 조회 API
export const weeklyOffSetting = async (
  groupId?: string
): Promise<WeeklyOffSettingConfig> => {
  return httpClient.get<WeeklyOffSettingConfig>("/weekly-off/settings", {
    params: groupId ? { group_id: groupId } : undefined,
  });
};

/**
 * 주휴 설정 저장 API
 * @param payload 저장할 주휴 설정 데이터
 * @param groupId (관리자용) 그룹 ID
 * @returns 저장된 주휴 설정 데이터
 */
export const saveWeeklyOffSetting = async (
  payload: Partial<WeeklyOffSettingConfig>,
  groupId?: string
): Promise<WeeklyOffSettingConfig> => {
  return httpClient.put<WeeklyOffSettingConfig>(
    "/weekly-off/settings",
    payload,
    {
      params: groupId ? { group_id: groupId } : undefined,
    }
  );
};

// 근무표 만들기 > 주휴 모달에서 사용 preview_weekday_label
// 간호사 주휴 설정 목록 및 미리보기 조회 API
export const weeklyOffNurses = async ({
  year,
  month,
  groupId,
}: {
  year: number;
  month: number;
  groupId?: string;
}): Promise<WeeklyOffNurseList> => {
  return httpClient.get<WeeklyOffNurseList>("/weekly-off/nurses", {
    params: { year, month, ...(groupId && { group_id: groupId }) },
  });
};

// 간호사별 주휴 설정 저장 API (저장 시점의 연/월이 기준 시점으로 갱신됨)
export const saveWeeklyOffNurses = async (
  payload: SaveWeeklyOffNursePayload[],
  groupId?: string
): Promise<WeeklyOffNurseList> => {
  return httpClient.put<WeeklyOffNurseList>(
    "/weekly-off/nurses",
    { items: payload },
    {
      params: groupId ? { group_id: groupId } : undefined,
    }
  );
};
